import { BBox } from '@turf/helpers';
import i18next from 'i18next';

import { GeoFilter, GeoFilterType } from '../../contexts/FilterContext';
import { GeoField, PeopleSnaphostGeoResult, PersonFilterInput } from '../../types/api.graphql';
import { hashCode } from '../helpers';
import { DrawProps, GeoFilterHelper, MarkerProps } from './GeoFilterHelper';

export interface GeoFilterListData {
  field: GeoField;
  values: string[];
}

export default class ListGeoFilterHelper extends GeoFilterHelper<GeoFilterListData> {
  generateId(): number {
    const valuesStr = this.geoFilter.data.values?.slice(0, 10).join(',') || '';
    const valuesLength = this.geoFilter.data.values?.length || 0;
    return hashCode(
      `${this.geoFilter.data.field},${valuesStr},${valuesLength},${(this.geoFilter.isParent || false).toString()},${(
        this.geoFilter.negate || false
      ).toString()}`,
    );
  }

  isEqual(geoFilter: GeoFilter): boolean {
    if (!super.isEqual(geoFilter)) return false;

    if (geoFilter.data.field !== this.geoFilter.data.field) return false;
    if (geoFilter.negate !== this.geoFilter.negate) return false;
    if (geoFilter.data.values?.length !== this.geoFilter.data.values?.length) return false;
    for (let i = 0; i < (geoFilter.data.values?.length || 0); i++) {
      if (geoFilter.data.values[i] !== this.geoFilter.data.values[i]) return false;
    }
    return true;
  }

  toGraphQLGeoFilter(skipExcludedZips?: boolean): PersonFilterInput {
    return {
      geoFilter: {
        operator: {
          in: {
            field: this.geoFilter.data.field,
            values: this.geoFilter.data.values,
          },
        },
        negate: this.geoFilter.negate || false,
        ...(!skipExcludedZips && { excludedZipCodes: this.geoFilter.excludedZipCodes }),
      },
    };
  }

  generateIdForGeoFilterGraphQl(): number {
    const valuesStr = this.geoFilterGraphQL.operator.in?.values?.slice(0, 10).join(',') || '';
    const valuesLength = this.geoFilterGraphQL.operator.in?.values?.length || 0;
    return hashCode(`${this.geoFilterGraphQL.operator.in?.field},${valuesStr},${valuesLength}`);
  }

  fromGraphQLGeoFilter(): GeoFilter<GeoFilterListData> {
    const data: GeoFilterListData = {
      field: this.geoFilterGraphQL.operator.in?.field,
      values: this.geoFilterGraphQL.operator.in?.values || [],
    };
    return {
      id: this.generateIdForGeoFilterGraphQl(),
      type: GeoFilterType.List,
      data,
      negate: this.geoFilterGraphQL.negate || false,
      excludedZipCodes: this.geoFilterGraphQL.excludedZipCodes,
    };
  }

  getInfoText(): string {
    const fieldLabel =
      this.geoFilter.data.field === GeoField.Dma
        ? i18next.t('geoFilters.listFieldDma')
        : i18next.t('geoFilters.listFieldZip');
    const condition = this.geoFilter.negate
      ? i18next.t('geoFilters.listConditionNotIn')
      : i18next.t('geoFilters.listConditionIn');
    return `${fieldLabel} ${condition} (${this.geoFilter.data.values?.length || 0} values)`;
  }

  geoFilterFromGeoResult = (geoResult: PeopleSnaphostGeoResult, countByPostalCode: object): GeoFilter => ({
    ...this.geoFilter,
    id: this.geoFilter.id || this.generateId(),
    zipCodes: geoResult.postalCodes.map((postalCode) => ({
      zip: postalCode,
      count: countByPostalCode[postalCode] || 0,
    })),
    count: geoResult.count,
  });

  getCenter() {
    return undefined;
  }

  getBoundingBox(): BBox {
    return undefined;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  drawLayer(props: DrawProps) {
    return {};
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  drawMarker(props: MarkerProps) {
    return undefined;
  }
}
